import type { Seller, Vehicle } from "@/api/types";

const usd = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });
const usdCompact = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", notation: "compact", maximumFractionDigits: 1 });
const num = new Intl.NumberFormat("en-US");

export const EMPTY = "—";

/** Integer cents → "$38,700". */
export function formatMoney(cents: number | null | undefined, opts?: { compact?: boolean }): string {
  if (cents === null || cents === undefined) return EMPTY;
  return (opts?.compact ? usdCompact : usd).format(cents / 100);
}

/** Signed difference in cents → "+$800" / "−$1,200". */
export function formatMoneyDelta(cents: number | null | undefined): string {
  if (cents === null || cents === undefined) return EMPTY;
  const sign = cents > 0 ? "+" : cents < 0 ? "−" : "";
  return `${sign}${usd.format(Math.abs(cents) / 100)}`;
}

export function formatNumber(n: number | null | undefined): string {
  return n === null || n === undefined ? EMPTY : num.format(n);
}

export function formatMiles(n: number | null | undefined): string {
  return n === null || n === undefined ? EMPTY : `${num.format(n)} mi`;
}

export function formatPercent(ratio: number | null | undefined, digits = 0): string {
  if (ratio === null || ratio === undefined) return EMPTY;
  return `${(ratio * 100).toFixed(digits)}%`;
}

type VehicleLike = Partial<Pick<Vehicle, "year" | "make" | "model" | "trim">> | null | undefined;

/** "2023 Toyota Tacoma TRD Pro" — or null when nothing is known yet. */
export function vehicleTitle(v: VehicleLike): string | null {
  if (!v) return null;
  const parts = [v.year, v.make, v.model, v.trim].filter((p) => p !== null && p !== undefined && p !== "");
  return parts.length ? parts.join(" ") : null;
}

type SellerLike = Partial<Pick<Seller, "firstName" | "lastName">> | null | undefined;

/** Full name, or null when unknown. */
export function sellerName(s: SellerLike): string | null {
  if (!s) return null;
  const name = [s.firstName, s.lastName].filter(Boolean).join(" ");
  return name || null;
}

/** "M. Reyes" — compact form for dense rows. */
export function sellerShortName(s: SellerLike): string | null {
  if (!s) return null;
  if (s.firstName && s.lastName) return `${s.firstName.charAt(0)}. ${s.lastName}`;
  return s.firstName ?? s.lastName ?? null;
}

export function initials(name: string | null | undefined): string {
  if (!name) return "?";
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p.charAt(0).toUpperCase())
    .join("");
}

/** +15125550123 → (512) 555-0123 */
export function formatPhone(e164: string | null | undefined): string {
  if (!e164) return EMPTY;
  const m = /^\+1(\d{3})(\d{3})(\d{4})$/.exec(e164);
  return m ? `(${m[1]}) ${m[2]}-${m[3]}` : e164;
}

/** Last 6 of the VIN, the way buyers read it out. */
export function shortVin(vin: string | null | undefined): string | null {
  return vin ? `…${vin.slice(-6)}` : null;
}

export function dollarsToCents(input: string): number | null {
  const cleaned = input.replace(/[$,\s]/g, "");
  if (!/^\d+(\.\d{1,2})?$/.test(cleaned)) return null;
  return Math.round(Number(cleaned) * 100);
}

export function formatBytes(bytes: number | null | undefined): string {
  if (bytes === null || bytes === undefined) return EMPTY;
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}
